/*
 * This code is protected by intellectual property rights.
 * Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
 * © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
 */

import { Injectable, inject } from '@angular/core'
import { PidxErrorService } from '@myprofile-ui/pidx-utils'
import { TranslateService } from '@ngx-translate/core'
import { Action, Selector, State, StateContext, Store } from '@ngxs/store'
import cloneDeep from 'lodash-es/cloneDeep'
import { combineLatest, firstValueFrom, of, throwError } from 'rxjs'
import { catchError, delay, mergeMap, map, tap } from 'rxjs/operators'
import { MyProfileAddressWidgetMode } from '../enums/myprofile-address-widget-mode'
import { MyProfileAddressDataService } from '../services/myprofile-address-data.service'
import { SetAppErrorMessage, SetAppLoadingStatus, SetEditorErrorMessage, SetInitialConfiguration } from './address-widget.actions'
import {
  AddSchemaForCountry,
  CreateAddress,
  DeleteAddress,
  GetSchemas,
  SetEditorLoadingStatus,
  SetEditorSchema,
  ShowBlankEditor,
  UpdateAddress,
  UpdateTmpAddress,
} from './editor.actions'
import {
  GetAddresses,
  GetProfile,
  SelectAddress,
  SetAddressBeingEdited,
  SetAddressListDisabled,
  SetAddressListOverlayLoadingStatus,
} from './list.actions'
import { AddressDto, AddressService } from '../../../../../apps/myprofile-ui/src/app/services/address/address.service'
import { ProfileService, PublicProfile } from '../../../../../apps/myprofile-ui/src/app/services/profile/profile.service'
import { HttpErrorResponse, HttpHeaders } from '@angular/common/http'
import { v4 as uuid } from 'uuid'

export interface AddressWidgetInstance {
  addressIdBeingEdited: string | null
  addressList: {
    disabled: boolean
    loading: boolean
    showDeleteButtons: boolean
    showEditButtons: boolean
    selectedAddressId: string | null
  }
  editor: {
    loading: boolean
    visible: boolean
    country: string
    tmpAddress: AddressDto | null
    errorMessage: string | null
    firstAddressInCheckout: boolean
  }
  errorMessage: string | null
  mode: MyProfileAddressWidgetMode
  newAddressId: string | null
  token: string
}

export interface AddressWidgetStateModel {
  isCnUser: boolean
  addresses: AddressDto[]
  country: string
  harmonizedLocale: string
  layouts: any
  loading: boolean
  locale: string
  schemas: any
  token: string
  widgetInstances: { [instanceId: string]: AddressWidgetInstance }
}

@State<AddressWidgetStateModel>({
  name: 'addressWidget',
  defaults: {
    isCnUser: false,

    loading: true,

    country: 'de',
    locale: 'de_DE',
    harmonizedLocale: 'de-DE',

    schemas: {},
    layouts: {},
    addresses: [],
    token: '',

    widgetInstances: {},
  },
})
@Injectable()
export class AddressWidgetState {
  private myProfileAddressDataService = inject(MyProfileAddressDataService)
  private profileService = inject(ProfileService)
  private addressService: AddressService = inject(AddressService)
  private pidxErrorService = inject(PidxErrorService)
  private _translateService = inject(TranslateService)
  private store = inject(Store)

  @Selector()
  static addressObjCurrentlyBeingEdited({ widgetInstances }: AddressWidgetStateModel, instanceId: string) {
    return widgetInstances[instanceId].editor.tmpAddress
  }

  @Selector()
  static editorIsLoading({ widgetInstances }: AddressWidgetStateModel, instanceId: string) {
    return widgetInstances[instanceId].editor.loading
  }

  static getStandardAddress(addresses: AddressDto[]): AddressDto | undefined {
    return addresses.find((address) => address.isStandard)
  }

  // ===================================================================================================================
  // Convenience selectors
  // ===================================================================================================================

  @Selector()
  static hasAddresses(state: AddressWidgetStateModel) {
    if (!state.addresses) {
      return false
    }

    return !!state.addresses.length
  }

  @Selector()
  static isBlankCheckoutModeEditor(state: AddressWidgetStateModel, instanceId: string) {
    return !state.addresses.length && state.widgetInstances[instanceId].mode === MyProfileAddressWidgetMode.CHECKOUT
  }

  @Selector()
  static layoutForCountry({ layouts, widgetInstances }: AddressWidgetStateModel, instanceId: string) {
    return layouts[widgetInstances[instanceId].editor.country.toUpperCase()]
  }

  @Selector()
  static schemaForCountry({ schemas, widgetInstances }: AddressWidgetStateModel, instanceId: string) {
    return schemas[widgetInstances[instanceId].editor.country.toUpperCase()]
  }

  // Helper fn to always sort our addresses the same way.
  static sortAddresses(addresses: AddressDto[]): AddressDto[] {
    return addresses
      .sort((addressItemA: AddressDto, addressItemB: AddressDto) => {
        return (addressItemA.addressId ?? '') < (addressItemB.addressId ?? '') ? -1 : 1
      })
      .reduce((addressItems: AddressDto[], addressItem) => {
        if (addressItem.isStandard) {
          return [addressItem, ...addressItems]
        } else {
          return [...addressItems, addressItem]
        }
      }, [] as AddressDto[])
  }

  // ===================================================================================================================
  // Global Action Listeners
  // ===================================================================================================================

  @Action(SetInitialConfiguration)
  SetInitialConfiguration(
    { patchState, getState }: StateContext<AddressWidgetStateModel>,
    { instanceId, mode, locale, country, token }: SetInitialConfiguration,
  ) {
    const { widgetInstances } = getState()

    /* As our locale is strictly speaking no correct, for example en_GB in japan should be en-JP
     Therefore we're harmonizing the locale here. We still need the incorrect one for the legacy api though */
    const harmonizedLocale = `${locale.replace('_', '-').split('-')[0]}-${country.toUpperCase()}`
    const newWidgetInstance = {
      token,

      mode: MyProfileAddressWidgetMode[mode as keyof typeof MyProfileAddressWidgetMode],
      errorMessage: null,
      addressIdBeingEdited: null,
      newAddressId: null,

      editor: {
        loading: false,
        visible: false,
        country,
        tmpAddress: null,
        errorMessage: null,
        firstAddressInCheckout: false,
      },

      addressList: {
        disabled: false,
        loading: false,
        showDeleteButtons: true,
        showEditButtons: true,

        selectedAddressId: null,
      },
    }

    return patchState({
      country,
      locale,
      token,
      harmonizedLocale,
      widgetInstances: {
        ...widgetInstances,
        [instanceId]: newWidgetInstance,
      },
    })
  }

  @Action(CreateAddress) createAddress({ getState }: StateContext<AddressWidgetStateModel>, { address, instanceId }: CreateAddress) {
    const { token, country, locale } = getState()
    const headers = this.buildHeaders(token)
    this.store.dispatch(new SetEditorLoadingStatus(true)).subscribe()

    return this.addressService.createAddressForProfile(country, locale, address, headers).pipe(
      mergeMap((newAddress: AddressDto) => {
        return combineLatest([of(newAddress.addressId as string), this.store.dispatch(new GetAddresses())])
      }),
      tap(([newAddressId]) => {
        this.postCRUDAddressCommands(newAddressId, instanceId)
      }),
      catchError((error) => {
        this.defaultErrorHandling(error, true, instanceId)
        return throwError(() => error)
      }),
    )
  }

  @Action(DeleteAddress) deleteAddress({ getState }: StateContext<AddressWidgetStateModel>, { addressId, instanceId }: DeleteAddress) {
    const { widgetInstances, token, country, locale } = getState()
    const headers = this.buildHeaders(token)
    this.store.dispatch(new SetAddressListOverlayLoadingStatus(true)).subscribe()

    const widgetInstance = Object.values(widgetInstances)[0]

    if (widgetInstance.mode === MyProfileAddressWidgetMode.CHECKOUT) {
      this.store.dispatch(new SetEditorLoadingStatus(true)).subscribe()
    } else {
      this.store.dispatch(new SetAppLoadingStatus(true)).subscribe()
    }

    return this.addressService.deleteAddressForProfile(country, locale, addressId, headers).pipe(
      mergeMap(() => this.store.dispatch(new GetAddresses())),
      tap(({ addressWidget }) => {
        const standardAddress = AddressWidgetState.getStandardAddress(addressWidget.addresses)
        this.postCRUDAddressCommands(standardAddress?.addressId as string, instanceId)
        this.store.dispatch(new SetAddressListOverlayLoadingStatus(false)).subscribe()
      }),
      catchError((error) => {
        this.store.dispatch(new SetAddressListOverlayLoadingStatus(false)).subscribe()

        if (widgetInstance.mode === MyProfileAddressWidgetMode.CHECKOUT) {
          this.defaultErrorHandling(error, true, instanceId)
        } else {
          this.defaultErrorHandling(error, false, instanceId)
        }
        return throwError(() => error)
      }),
    )
  }

  @Action(GetAddresses) getAddresses({ patchState, getState }: StateContext<AddressWidgetStateModel>) {
    const { token, country, locale } = getState()
    const headers = this.buildHeaders(token)
    return this.addressService.getAllAddressesForProfile(country, locale, headers).pipe(
      tap((addresses: AddressDto[]) => {
        patchState({
          addresses: AddressWidgetState.sortAddresses(addresses),
        })
        return this.store.dispatch(new GetSchemas()).subscribe()
      }),
      catchError((error) => {
        this.defaultErrorHandling(error)
        return throwError(() => error)
      }),
    )
  }

  @Action(GetProfile) getProfile({ patchState, getState }: StateContext<AddressWidgetStateModel>) {
    const { token, country, locale } = getState()
    const headers = this.buildHeaders(token)
    return this.profileService.getProfile(country, locale, headers).pipe(
      tap((profile: PublicProfile) => {
        return patchState({
          isCnUser: ['CN'].includes(profile.preferredCountry ?? '') || false,
        })
      }),
      catchError((error) => {
        this.defaultErrorHandling(error)
        return throwError(() => error)
      }),
    )
  }

  // ===================================================================================================================
  // Address List Action Listeners

  @Action(AddSchemaForCountry) getSchemaForCountry(
    { patchState, getState }: StateContext<AddressWidgetStateModel>,
    { country }: AddSchemaForCountry,
  ) {
    const state = getState()
    const { widgetInstances } = state

    if (Object.prototype.hasOwnProperty.call(state.schemas, country)) {
      return of(state).pipe(delay(50))
    } else {
      return combineLatest([
        this.myProfileAddressDataService.getSchema(country, 'address', state.locale).pipe(
          map((schema) => {
            const schemas: { [key: string]: unknown } = {}
            schemas[country] = schema
            return schemas
          }),
        ),
        this.myProfileAddressDataService.getLayout(country, 'address').pipe(
          map((layout) => {
            const layouts: { [key: string]: unknown } = {}
            layouts[country] = layout
            return layouts
          }),
        ),
      ]).pipe(
        tap(([schemas, layouts]) => {
          const widgetInstancesToPatch: { [key: string]: AddressWidgetInstance } = {}
          Object.entries(widgetInstances).forEach(([instanceId, widgetInstance]) => {
            widgetInstancesToPatch[instanceId] = {
              ...widgetInstance,
              editor: {
                ...widgetInstance.editor,
                loading: false,
              },
            }
          })

          patchState({
            schemas: {
              ...state.schemas,
              ...schemas,
            },
            layouts: {
              ...state.layouts,
              ...layouts,
            },
            widgetInstances: {
              ...widgetInstancesToPatch,
            },
          })
        }),
      )
    }
  }

  @Action(GetSchemas)
  async getSchemas({ patchState, getState }: StateContext<AddressWidgetStateModel>) {
    const { addresses, locale, country } = getState()

    if (!addresses) {
      return of(false)
    }

    const countries: string[] = addresses.map((address) => address.country).filter((c): c is string => !!c)
    countries.push(country.toUpperCase())
    const uniqueCountries = countries.filter((item, pos) => countries.indexOf(item) == pos)

    const schemaPromises: Promise<any>[] = uniqueCountries.map((c) =>
      firstValueFrom(this.myProfileAddressDataService.getSchema(c, 'address', locale)),
    )
    const layoutPromises: Promise<any>[] = uniqueCountries.map((c) =>
      firstValueFrom(this.myProfileAddressDataService.getLayout(c, 'address')),
    )
    const translationPromise = firstValueFrom(this.myProfileAddressDataService.getTranslations())

    const resolvedSchemas = await Promise.all(schemaPromises)
    const schemas: { [key: string]: object } = {}
    uniqueCountries.forEach((c, index) => {
      schemas[c] = resolvedSchemas[index]
    })

    const resolvedLayouts = await Promise.all(layoutPromises)
    const layouts: { [key: string]: object } = {}
    uniqueCountries.forEach((c, index) => {
      layouts[c] = resolvedLayouts[index]
    })

    const translations = await translationPromise

    const { widgetInstances } = getState()

    const widgetInstancesToPatch: { [key: string]: AddressWidgetInstance } = {}
    Object.entries(widgetInstances).forEach(([instanceId, widgetInstance]) => {
      if (widgetInstance.mode !== MyProfileAddressWidgetMode.PROFILE) {
        const lang = locale.split('_')[0]
        this._translateService.setFallbackLang(lang)
        this._translateService.setTranslation(lang, translations as { [key: string]: any }, true)
      }

      widgetInstancesToPatch[instanceId] = {
        ...widgetInstance,
        addressList: {
          ...widgetInstance.addressList,
          disabled: false,
        },
      }
    })

    patchState({
      widgetInstances: {
        ...widgetInstancesToPatch,
      },
      schemas,
      layouts,
      loading: false,
    })
  }

  @Action(SelectAddress) selectAddress(
    { patchState, getState }: StateContext<AddressWidgetStateModel>,
    { selectedAddressId, instanceId }: SelectAddress,
  ) {
    const { widgetInstances } = getState()
    const currentInstance = widgetInstances[instanceId]

    const widgetInstanceToPatch = {
      ...currentInstance,
      addressList: {
        ...currentInstance.addressList,
        selectedAddressId,
      },
    }

    return patchState({
      widgetInstances: {
        ...widgetInstances,
        [instanceId]: {
          ...widgetInstanceToPatch,
        },
      },
    })
  }

  @Action(SetAddressBeingEdited) setAddressBeingEdited(
    { patchState, getState }: StateContext<AddressWidgetStateModel>,
    { addressIdBeingEdited, instanceId }: SetAddressBeingEdited,
  ) {
    const { widgetInstances, addresses } = getState()
    const currentInstance = widgetInstances[instanceId]

    const tmpAddress = cloneDeep(addresses.find((address: AddressDto) => address.addressId === addressIdBeingEdited))
    const country = tmpAddress?.country ?? ''

    const widgetInstanceToPatch = {
      ...currentInstance,
      errorMessage: null,
      addressIdBeingEdited,
      editor: {
        ...currentInstance.editor,
        country,
        tmpAddress: tmpAddress ?? null,
        visible: true,
      },
      addressList: {
        ...currentInstance.addressList,
        disabled: true,
      },
    }

    patchState({
      widgetInstances: {
        ...widgetInstances,
        [instanceId]: {
          ...widgetInstanceToPatch,
        },
      },
    })
  }

  // ===================================================================================================================
  // Editor Action Listeners
  // ===================================================================================================================

  @Action(SetAddressListDisabled) setAddressListDisabled(
    { patchState, getState }: StateContext<AddressWidgetStateModel>,
    { disabled, instanceId }: SetAddressListDisabled,
  ) {
    const { widgetInstances } = getState()
    const currentInstance = widgetInstances[instanceId]

    const widgetInstanceToPatch = {
      ...currentInstance,
      addressList: {
        ...currentInstance.addressList,
        disabled,
      },
    }

    patchState({
      widgetInstances: {
        ...widgetInstances,
        [instanceId]: {
          ...widgetInstanceToPatch,
        },
      },
    })
  }

  @Action(SetAppErrorMessage) setAppErrorMessage(
    { patchState, getState }: StateContext<AddressWidgetStateModel>,
    { errorMessage, instanceId }: SetAppErrorMessage,
  ) {
    const key = instanceId ?? ''
    const { widgetInstances } = getState()
    const widgetInstanceToPatch = {
      ...widgetInstances[key],
      errorMessage,
    }

    patchState({
      widgetInstances: {
        ...widgetInstances,
        [key]: {
          ...widgetInstanceToPatch,
        },
      },
    })
  }

  @Action(SetAppLoadingStatus) setAppIsLoading({ patchState }: StateContext<AddressWidgetStateModel>, { loading }: SetAppLoadingStatus) {
    patchState({ loading })
  }

  @Action(SetEditorErrorMessage) setEditorErrorMessage(
    { patchState, getState }: StateContext<AddressWidgetStateModel>,
    { errorMessage, instanceId }: SetEditorErrorMessage,
  ) {
    const key = instanceId ?? ''
    const { widgetInstances } = getState()
    const currentInstance = widgetInstances[key]

    const widgetInstanceToPatch = {
      ...currentInstance,
      errorMessage: null,
      editor: {
        ...currentInstance.editor,
        errorMessage,
      },
    }

    patchState({
      widgetInstances: {
        ...widgetInstances,
        [key]: {
          ...widgetInstanceToPatch,
        },
      },
    })
  }

  @Action(SetEditorLoadingStatus) setEditorLoadingStatus(
    { patchState, getState }: StateContext<AddressWidgetStateModel>,
    { loading }: SetEditorLoadingStatus,
  ) {
    const { widgetInstances } = getState()

    const widgetInstancesToPatch: { [key: string]: AddressWidgetInstance } = {}
    Object.entries(widgetInstances).forEach(([instanceId, widgetInstance]) => {
      widgetInstancesToPatch[instanceId] = {
        ...widgetInstance,
        editor: {
          ...widgetInstance.editor,
          loading,
        },
      }
    })

    patchState({
      widgetInstances: {
        ...widgetInstancesToPatch,
      },
    })
  }

  @Action(SetEditorSchema) setEditorSchema(
    { patchState, getState }: StateContext<AddressWidgetStateModel>,
    { country, instanceId }: SetEditorSchema,
  ) {
    const { widgetInstances } = getState()
    const currentInstance = widgetInstances[instanceId]

    const widgetInstanceToPatch = {
      ...currentInstance,
      editor: {
        ...currentInstance.editor,
        country,
        tmpAddress: {
          ...currentInstance.editor.tmpAddress,
          country,
        },
      },
    }

    patchState({
      widgetInstances: {
        ...widgetInstances,
        [instanceId]: {
          ...widgetInstanceToPatch,
        },
      },
    })
  }

  // ===================================================================================================================
  @Action(SetAddressListOverlayLoadingStatus) setListOverlayLoadingStatus(
    { patchState, getState }: StateContext<AddressWidgetStateModel>,
    { loading }: SetAddressListOverlayLoadingStatus,
  ) {
    const { widgetInstances } = getState()
    const widgetInstancesToPatch: { [key: string]: AddressWidgetInstance } = {}

    Object.entries(widgetInstances).forEach(([instanceId, widgetInstance]) => {
      widgetInstancesToPatch[instanceId] = {
        ...widgetInstance,
        addressList: {
          ...widgetInstance.addressList,
          loading,
        },
      }
    })

    patchState({
      widgetInstances: {
        ...widgetInstancesToPatch,
      },
    })
  }

  @Action(ShowBlankEditor) showBlankEditor(
    { patchState, getState }: StateContext<AddressWidgetStateModel>,
    { visible, instanceId }: ShowBlankEditor,
  ) {
    const { country, widgetInstances } = getState()
    const currentInstance = widgetInstances[instanceId]

    // Initially we pre-select country of the schema as the current country of the dropdown
    // Default usage is home
    const tmpAddress: AddressDto = {
      country: country.toUpperCase(),
      usage: 'home',
    }

    const widgetInstanceToPatch = {
      ...currentInstance,
      errorMessage: null,
      editor: {
        ...currentInstance.editor,
        visible,
        country: country.toUpperCase(),
        tmpAddress: visible ? tmpAddress : null,
        errorMessage: visible ? currentInstance.editor.errorMessage : null,
      },
      addressList: {
        ...currentInstance.addressList,
        disabled: visible,
      },
      addressIdBeingEdited: null,
    }

    patchState({
      widgetInstances: {
        ...widgetInstances,
        [instanceId]: {
          ...widgetInstanceToPatch,
        },
      },
    })
  }

  @Action(UpdateAddress) updateAddress(
    { patchState, getState }: StateContext<AddressWidgetStateModel>,
    { address, instanceId }: UpdateAddress,
  ) {
    const { token, country, locale } = getState()
    const headers = this.buildHeaders(token)
    this.store.dispatch(new SetEditorLoadingStatus(true)).subscribe()

    return this.addressService.updateAddressForProfile(country, locale, address, headers).pipe(
      mergeMap((updatedAddress: AddressDto) => {
        return combineLatest([of(updatedAddress.addressId as string), this.store.dispatch(new GetAddresses())])
      }),
      tap(([addressId]) => {
        this.postCRUDAddressCommands(addressId, instanceId)
      }),
      catchError((error) => {
        this.defaultErrorHandling(error, true, instanceId)
        return throwError(() => error)
      }),
    )
  }

  @Action(UpdateTmpAddress) updateTmpAddress(
    { patchState, getState }: StateContext<AddressWidgetStateModel>,
    { tempAddress, instanceId }: UpdateTmpAddress,
  ) {
    const { widgetInstances } = getState()
    const currentInstance = widgetInstances[instanceId]

    if (currentInstance?.editor?.tmpAddress && currentInstance?.editor?.tmpAddress?.country !== tempAddress.country) {
      const widgetInstanceToPatch = {
        ...currentInstance,
        editor: {
          ...currentInstance.editor,
          tmpAddress: {
            addressId: currentInstance.editor.tmpAddress.addressId,
            ciamId: currentInstance.editor.tmpAddress.ciamId,
            type: currentInstance.editor.tmpAddress.type,
            usage: tempAddress.usage,
            isStandard: currentInstance.editor.tmpAddress.isStandard,
            country: tempAddress.country,
          },
        },
      }
      patchState({
        widgetInstances: {
          ...widgetInstances,
          [instanceId]: {
            ...widgetInstanceToPatch,
          },
        },
      })
    } else {
      const widgetInstanceToPatch = {
        ...currentInstance,
        editor: {
          ...currentInstance.editor,
          tmpAddress: {
            ...(currentInstance.editor.tmpAddress ?? {}),
            ...tempAddress,
          },
        },
      }

      patchState({
        widgetInstances: {
          ...widgetInstances,
          [instanceId]: {
            ...widgetInstanceToPatch,
          },
        },
      })
    }
  }

  private defaultErrorHandling(error: HttpErrorResponse | null, isEditMode = false, instanceId: string | null = null) {
    const errorKey = this.pidxErrorService.getErrorKeyForHttpErrorResponseByCode(error, 'pidpaw.editAddress.error.backend.default')
    if (isEditMode) {
      this.store.dispatch(new SetEditorErrorMessage(errorKey, instanceId)).subscribe()
    } else {
      this.store.dispatch(new SetAppErrorMessage(errorKey, instanceId)).subscribe()
    }
    this.store.dispatch(new SetAppLoadingStatus(false)).subscribe()
    this.store.dispatch(new SetEditorLoadingStatus(false)).subscribe()
  }

  // We dont want any loading nor error state when successfully executing a CRUD-Action
  private postCRUDAddressCommands(addressId: string, instanceId: string) {
    const addresses = this.store.selectSnapshot(({ addressWidget }) => addressWidget.addresses)
    const widgetInstanceKeys = this.store.selectSnapshot(({ addressWidget }) => Object.keys(addressWidget.widgetInstances))

    if (addresses.length === 1) {
      widgetInstanceKeys.forEach((instanceKey) => {
        this.store.dispatch(new SelectAddress(addressId, instanceKey)).subscribe()
      })
    } else {
      this.store.dispatch(new SelectAddress(addressId, instanceId)).subscribe()
    }

    widgetInstanceKeys.forEach((instanceKey) => {
      this.store.dispatch(new ShowBlankEditor(false, instanceKey)).subscribe()
    })

    this.store.dispatch(new SetEditorLoadingStatus(false)).subscribe()
    this.store.dispatch(new SetAppLoadingStatus(false)).subscribe()
    this.store.dispatch(new SetAppErrorMessage(null, instanceId)).subscribe()
    this.store.dispatch(new SetEditorErrorMessage(null, instanceId)).subscribe()
  }

  private buildHeaders(token?: string) {
    if (!token) {
      return new HttpHeaders()
    }

    return new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'X-TRACE-ID': `MYPROFILE-PHONE-WIDGET-${uuid()}`,
    })
  }
}
